import React, { useState, useRef, useEffect, useCallback } from 'react';
import ConversationArea from './ConversationArea';
import YogaLogo from './YogaLogo';
import LeadCaptureForm from './LeadCaptureForm';
import smartChatService from '../../services/smartChatService';
import './ChatInterface.css';

const TRANSLATIONS = {
  English: {
    title: "Yoga Assistant",
    online: "Online",
    connecting: "Connecting...",
    reconnecting: "Reconnecting...",
    offline: "Offline",
    error: "Connection Error",
    muteAudio: "Mute audio",
    enableAudio: "Enable audio",
    selectLanguage: "Select language",
    closeChat: "Close chat",
    retry: "Retry",
    placeholderConnected: "Type your message...",
    placeholderConnecting: "Connecting...",
    placeholderOffline: "Connection required to send messages",
    stopRecording: "Stop recording",
    startVoiceInput: "Start voice input",
    sendMessage: "Send message",
    footer: "Powered by AI • Ask me about yoga classes and benefits",
    voiceError: "Voice input error. Please check your microphone.",
    connectionLost: "Connection lost. Attempting to reconnect...",
    listening: "Listening...",
    listeningHint: "Speak now..."
  },
  Hindi: {
    title: "योग सहायक",
    online: "ऑनलाइन",
    connecting: "जुड़ रहा है...",
    reconnecting: "पुनः जुड़ रहा है...",
    offline: "ऑफ़लाइन",
    error: "कनेक्शन त्रुटि",
    muteAudio: "ऑडियो म्यूट करें",
    enableAudio: "ऑडियो सक्षम करें",
    selectLanguage: "भाषा चुनें",
    closeChat: "चैट बंद करें",
    retry: "पुनः प्रयास करें",
    placeholderConnected: "अपना संदेश टाइप करें...",
    placeholderConnecting: "जुड़ रहा है...",
    placeholderOffline: "संदेश भेजने के लिए कनेक्शन आवश्यक है",
    stopRecording: "रिकॉर्डिंग रोकें",
    startVoiceInput: "वॉइस इनपुट शुरू करें",
    sendMessage: "संदेश भेजें",
    footer: "AI द्वारा संचालित • योग कक्षाओं और लाभों के बारे में पूछें",
    voiceError: "वॉइस इनपुट त्रुटि। कृपया अपना माइक्रोफ़ोन जांचें।",
    connectionLost: "कनेक्शन टूट गया। पुनः जुड़ने का प्रयास कर रहा है...",
    listening: "सुन रहा हूँ...",
    listeningHint: "अब बोलें..."
  }
};

const ChatInterface = ({ isOpen, onClose, sessionId: initialSessionId, onSessionCreated }) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [sessionId, setSessionId] = useState(initialSessionId);
  const [error, setError] = useState(null);
  const [language, setLanguage] = useState('English');
  const [isListening, setIsListening] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const leadFormShownRef = useRef(false);
  const inputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const isUserTyping = useRef(false);
  const recognitionRef = useRef(null);
  const synthesisRef = useRef(window.speechSynthesis);
  const lastInputSourceRef = useRef('text');

  const t = TRANSLATIONS[language] || TRANSLATIONS['English'];

  // Load language preference from localStorage
  useEffect(() => {
    try {
      const storedPrefs = localStorage.getItem('yoga-chat-user-preferences');
      if (storedPrefs) {
        const prefs = JSON.parse(storedPrefs);
        if (prefs.language) {
          setLanguage(prefs.language);
        }
      }
    } catch (e) {
      console.error('Error loading preferences:', e);
    }
  }, []);

  // Update language preference
  const handleLanguageChange = (newLanguage) => {
    setLanguage(newLanguage);
    try {
      const storedPrefs = localStorage.getItem('yoga-chat-user-preferences');
      const prefs = storedPrefs ? JSON.parse(storedPrefs) : {};
      const updatedPrefs = { ...prefs, language: newLanguage };
      localStorage.setItem('yoga-chat-user-preferences', JSON.stringify(updatedPrefs));
    } catch (e) {
      console.error('Error saving language preference:', e);
    }
  };

  // Initialize Speech Recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;

      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setInputValue(transcript);
        lastInputSourceRef.current = 'voice';
        // Optional: Auto-send after voice input? For now, let user confirm.
        // handleSendMessage(new Event('submit')); 
        setIsListening(false);
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error', event.error);
        setIsListening(false);
        setError(t.voiceError);
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    }
  }, [t.voiceError]);

  // Update Recognition Language
  useEffect(() => {
    if (recognitionRef.current) {
      recognitionRef.current.lang = language === 'Hindi' ? 'hi-IN' : 'en-US';
    }
  }, [language]);

  // Toggle listening
  const toggleListening = () => {
    if (isListening) {
      recognitionRef.current?.stop();
    } else {
      setError(null);
      recognitionRef.current?.start();
      setIsListening(true);
    }
  };

  // Text to Speech
  const speakText = useCallback((text) => {
    if (!audioEnabled || !synthesisRef.current) return;

    // Cancel existing speech
    synthesisRef.current.cancel();

    const utterance = new SpeechSynthesisUtterance(text);

    // Select Voice
    const voices = synthesisRef.current.getVoices();
    let voice = null;

    if (language === 'Hindi') {
      voice = voices.find(v => v.lang.includes('hi')) || voices.find(v => v.name.includes('Hindi'));
    } else {
      voice = voices.find(v => v.lang === 'en-US' && !v.name.includes('Google')) || voices.find(v => v.lang.includes('en'));
    }

    if (voice) utterance.voice = voice;

    utterance.onstart = () => { };
    utterance.onend = () => { };
    utterance.onerror = () => { };

    synthesisRef.current.speak(utterance);
  }, [audioEnabled, language]);

  // Stop speaking when chat closes
  useEffect(() => {
    if (!isOpen) {
      synthesisRef.current?.cancel();
    }
  }, [isOpen]);



  // Socket event handlers
  const handleSessionJoined = useCallback((data) => {
    console.log('Session joined:', data);

    // Prevent duplicate session handling
    if (sessionId === data.sessionId) {
      console.log('ChatInterface: Session already set, ignoring duplicate');
      return;
    }

    setSessionId(data.sessionId);
    setMessages(data.messages || []);
    setConnectionStatus('connected');
    setError(null);

    // Notify parent component about session creation
    if (onSessionCreated && data.sessionId !== initialSessionId) {
      onSessionCreated(data.sessionId);
    }
  }, [onSessionCreated, initialSessionId, sessionId]);

  const handleMessage = useCallback((message) => {
    console.log('Message received:', message);
    setMessages(prev => {
      // Check if message already exists to prevent duplicates
      const exists = prev.some(msg => msg.id === message.id);
      if (exists) return prev;

      return [...prev, {
        ...message,
        timestamp: new Date(message.timestamp)
      }];
    });

    // Hide typing indicator when bot message arrives
    if (message.sender === 'bot') {
      setIsTyping(false);
      setIsLoading(false);

      // Save personalization data if available
      if (message.metadata?.personalization) {
        const { userProfile } = message.metadata.personalization;
        if (userProfile?.userPreferences) {
          localStorage.setItem('yoga-chat-user-preferences', JSON.stringify(userProfile.userPreferences));
        }
        if (userProfile?.conversationSummary) {
          localStorage.setItem('yoga-chat-conversation-context', JSON.stringify({
            summary: userProfile.conversationSummary,
            topics: userProfile.conversationTopics,
            lastUpdate: new Date().toISOString()
          }));
        }
        localStorage.setItem('yoga-chat-session-timestamp', Date.now().toString());
      }

      // Speak response if last input was voice
      if (lastInputSourceRef.current === 'voice') {
        speakText(message.content);
      }

      // Show lead form once per session when booking intent detected
      if (!leadFormShownRef.current) {
        const bookingKeywords = ['book', 'enroll', 'join', 'trial', 'register', 'schedule', 'call', '9595562223'];
        const contentLower = (message.content || '').toLowerCase();
        const hasBookingIntent = bookingKeywords.some(kw => contentLower.includes(kw));
        if (hasBookingIntent) {
          leadFormShownRef.current = true;
          setTimeout(() => setShowLeadForm(true), 1200);
        }
      }
    }
  }, [speakText]);

  const handleTyping = useCallback((data) => {
    if (data.isTyping !== undefined) {
      setIsTyping(data.isTyping);
    }
  }, []);

  const handleSocketError = useCallback((error) => {
    console.error('Socket error:', error);
    setError(error.message || 'Connection error occurred');
    setIsLoading(false);
    setIsTyping(false);
  }, []);

  const handleConnectionError = useCallback((data) => {
    console.error('Connection error:', data.error);
    setConnectionStatus('error');
    setError(data.error || 'Failed to connect to chat service');
  }, []);

  const handleDisconnect = useCallback((data) => {
    console.log('Socket disconnected:', data.reason);
    setConnectionStatus('disconnected');
    if (data.reason !== 'io client disconnect') {
      setError(TRANSLATIONS[language]?.connectionLost || 'Connection lost. Attempting to reconnect...');
    }
  }, [language]);

  const handleReconnect = useCallback((data) => {
    console.log('Socket reconnected:', data.attemptNumber);
    setConnectionStatus('connected');
    setError(null);
  }, []);

  const handleReconnectAttempt = useCallback((data) => {
    console.log('Reconnection attempt:', data.attemptNumber);
    setConnectionStatus('reconnecting');
    setError(`Reconnecting... (attempt ${data.attemptNumber})`);
  }, []);

  // Keep track of connection status in a ref for async operations
  const connectionStatusRef = useRef(connectionStatus);

  useEffect(() => {
    connectionStatusRef.current = connectionStatus;
  }, [connectionStatus]);

  // Initialize socket connection when chat opens
  useEffect(() => {
    if (!isOpen) return;

    let isInitializing = false;

    const initializeSocket = async () => {
      if (isInitializing) {
        console.log('ChatInterface: Already initializing, skipping...');
        return;
      }

      isInitializing = true;

      try {
        setConnectionStatus('connecting');
        setError(null);

        console.log('ChatInterface: Initializing chat service...');

        // Add timeout to prevent hanging
        const initPromise = (async () => {
          // Connect to chat service
          console.log('ChatInterface: Connecting to chat service...');
          await smartChatService.connect();
          console.log('ChatInterface: Chat service connected');

          // Set up event listeners
          smartChatService.on('session_joined', handleSessionJoined);
          smartChatService.on('message', handleMessage);
          smartChatService.on('typing', handleTyping);
          smartChatService.on('error', handleSocketError);
          smartChatService.on('connect_error', handleConnectionError);
          smartChatService.on('disconnect', handleDisconnect);
          smartChatService.on('reconnect', handleReconnect);
          smartChatService.on('reconnect_attempt', handleReconnectAttempt);

          // Join session
          console.log('ChatInterface: Joining session with ID:', sessionId);
          await smartChatService.joinSession(sessionId);
          console.log('ChatInterface: Session join completed');

          // Fallback: If we get here but no session_joined event fired, manually set connected
          setTimeout(() => {
            if (connectionStatusRef.current === 'connecting') {
              console.log('ChatInterface: Fallback - manually setting connected state');
              setConnectionStatus('connected');
              setError(null);
            }
          }, 2000);
        })();

        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Connection timeout after 60 seconds - Server might be waking up')), 60000);
        });

        await Promise.race([initPromise, timeoutPromise]);

      } catch (error) {
        console.error('Failed to initialize chat:', error);
        setConnectionStatus('error');
        setError(`Failed to connect to chat service: ${error.message}`);

        // Fallback: Try to set connected state anyway if we have a connection
        if (smartChatService.isHealthy()) {
          console.log('ChatInterface: Service is healthy, setting connected state as fallback');
          setConnectionStatus('connected');
          setError(null);
        }
      } finally {
        isInitializing = false;
      }
    };

    initializeSocket();

    // Cleanup function
    return () => {
      smartChatService.off('session_joined', handleSessionJoined);
      smartChatService.off('message', handleMessage);
      smartChatService.off('typing', handleTyping);
      smartChatService.off('error', handleSocketError);
      smartChatService.off('connect_error', handleConnectionError);
      smartChatService.off('disconnect', handleDisconnect);
      smartChatService.off('reconnect', handleReconnect);
      smartChatService.off('reconnect_attempt', handleReconnectAttempt);
    };
  }, [isOpen, sessionId, handleSessionJoined, handleMessage, handleTyping, handleSocketError, handleConnectionError, handleDisconnect, handleReconnect, handleReconnectAttempt]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current && connectionStatus === 'connected') {
      inputRef.current.focus();
    }
  }, [isOpen, connectionStatus]);

  // Handle user typing indicators
  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    lastInputSourceRef.current = 'text';

    // Send typing indicator
    if (connectionStatus === 'connected') {
      if (value.trim() && !isUserTyping.current) {
        isUserTyping.current = true;
        smartChatService.setTyping(true);
      } else if (!value.trim() && isUserTyping.current) {
        isUserTyping.current = false;
        smartChatService.setTyping(false);
      }

      // Clear existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      // Set timeout to stop typing indicator
      if (value.trim()) {
        typingTimeoutRef.current = setTimeout(() => {
          if (isUserTyping.current) {
            isUserTyping.current = false;
            smartChatService.setTyping(false);
          }
        }, 1000);
      }
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputValue.trim() || isLoading || connectionStatus !== 'connected') return;

    try {
      const message = inputValue.trim();
      setInputValue('');
      setIsLoading(true);
      setError(null);

      // Stop typing indicator
      if (isUserTyping.current) {
        isUserTyping.current = false;
        smartChatService.setTyping(false);
      }

      // Clear typing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      // Send message via chat service
      smartChatService.sendMessage(message, 'text', { language });

    } catch (error) {
      console.error('Error sending message:', error);
      setError('Failed to send message. Please try again.');
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  const handleSuggestionClick = useCallback((suggestion) => {
    if (connectionStatus === 'connected' && !isLoading) {
      console.log('ChatInterface: Suggestion clicked:', suggestion);
      setInputValue(suggestion);
      inputRef.current?.focus();
    }
  }, [connectionStatus, isLoading]);

  // Cleanup typing timeout on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  if (!isOpen) return null;

  return (
    <div className="chat-interface-overlay">
      <div className="chat-interface">

        {/* Voice Listening Overlay */}
        {isListening && (
          <div className="voice-listening-overlay" onClick={toggleListening}>
            <div className="voice-visualizer">
              <div className="voice-circle ring-1"></div>
              <div className="voice-circle ring-2"></div>
              <div className="voice-circle ring-3"></div>
              <div className="mic-icon-large">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                  <line x1="12" y1="19" x2="12" y2="23" />
                  <line x1="8" y1="23" x2="16" y2="23" />
                </svg>
              </div>
            </div>
            <p className="listening-text">{t.listening}</p>
            <p className="listening-hint">{t.listeningHint}</p>
          </div>
        )}

        {/* Header */}
        <div className="chat-header">
          <div className="chat-header-info">
            <div className="bot-avatar">
              <YogaLogo size={32} />
            </div>
            <div className="bot-info">
              <h4>{t.title}</h4>
              <span className={`bot-status ${connectionStatus}`}>
                {connectionStatus === 'connected' && t.online}
                {connectionStatus === 'connecting' && t.connecting}
                {connectionStatus === 'reconnecting' && t.reconnecting}
                {connectionStatus === 'disconnected' && t.offline}
                {connectionStatus === 'error' && t.error}
              </span>
            </div>
          </div>
          <div className="chat-header-actions">
            <button
              className={`icon-button ${audioEnabled ? 'active' : ''}`}
              onClick={() => {
                setAudioEnabled(!audioEnabled);
                if (audioEnabled) synthesisRef.current?.cancel();
              }}
              aria-label={audioEnabled ? t.muteAudio : t.enableAudio}
            >
              {audioEnabled ? (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" xmlns="http://www.w3.org/2000/svg">
                  <path d="M11 5L6 9H2V15H6L11 19V5Z" />
                  <path d="M19.07 4.93L15.54 8.46" />
                  <path d="M15.54 15.54L19.07 19.07" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" xmlns="http://www.w3.org/2000/svg">
                  <path d="M11 5L6 9H2V15H6L11 19V5Z" />
                  <line x1="23" y1="9" x2="17" y2="15" />
                  <line x1="17" y1="9" x2="23" y2="15" />
                </svg>
              )}
            </button>
            <select
              value={language}
              onChange={(e) => handleLanguageChange(e.target.value)}
              className="language-selector"
              aria-label={t.selectLanguage}
            >
              <option value="English">English</option>
              <option value="Hindi">हिंदी</option>
            </select>
            <button
              className="close-button"
              onClick={onClose}
              aria-label={t.closeChat}
            >
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </div>

        {/* Connection Error Banner */}
        {error && (
          <div className="connection-error">
            <span>{error}</span>
            {connectionStatus === 'error' && (
              <button
                onClick={() => window.location.reload()}
                className="retry-button"
              >
                {t.retry}
              </button>
            )}
          </div>
        )}

        {/* Messages Area */}
        <ConversationArea
          messages={messages}
          isTyping={isTyping}
          isLoading={isLoading}
          onSuggestionClick={handleSuggestionClick}
          autoScroll={true}
        />

        {/* Lead Capture Form */}
        {showLeadForm && (
          <div style={{ padding: '0 12px' }}>
            <LeadCaptureForm
              sessionId={sessionId}
              onClose={() => setShowLeadForm(false)}
              onSuccess={() => {
                setShowLeadForm(false);
              }}
            />
          </div>
        )}

        {/* Input Area */}
        <div className="chat-input-area">
          <form onSubmit={handleSendMessage} className="chat-input-form">
            <div className="input-container">
              <textarea
                ref={inputRef}
                value={inputValue}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                placeholder={
                  connectionStatus === 'connected'
                    ? t.placeholderConnected
                    : connectionStatus === 'connecting'
                      ? t.placeholderConnecting
                      : t.placeholderOffline
                }
                className="chat-input"
                rows="1"
                disabled={isLoading || connectionStatus !== 'connected'}
              />
              <button
                type="button"
                className={`mic-button ${isListening ? 'listening' : ''}`}
                onClick={toggleListening}
                disabled={isLoading || connectionStatus !== 'connected'}
                aria-label={isListening ? t.stopRecording : t.startVoiceInput}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                  <line x1="12" y1="19" x2="12" y2="23" />
                  <line x1="8" y1="23" x2="16" y2="23" />
                </svg>
              </button>
              <button
                type="submit"
                className="send-button"
                disabled={!inputValue.trim() || isLoading || connectionStatus !== 'connected'}
                aria-label={t.sendMessage}
              >
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M2 21L23 12L2 3V10L17 12L2 14V21Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
            </div>
          </form>
          <div className="chat-footer">
            <span>{t.footer}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatInterface;